import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Home, ChildCare, Block } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import MaterialReactTable from 'material-react-table';
import toast from 'react-hot-toast';
import { AdminUserSearchService, AdminUserSearchResult } from 'us-web-services';

const useStyles = makeStyles()(theme => ({
  container: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    marginBottom: theme.spacing(2),
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  avatar: {
    width: 32,
    height: 32,
  },
  deactivated: {
    color: theme.palette.error.main,
  },
  chip: {
    height: 22,
    fontSize: '0.7rem',
  },
  copyButton: {
    padding: 4,
  },
  noResults: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

type UserType = 'all' | 'parent' | 'sitter' | 'agency';

interface Props {
  searchTerm: string;
  onSelectUser: (user: AdminUserSearchResult) => void;
}

function UserSearchResults({ searchTerm, onSelectUser }: Props): JSX.Element | null {
  const { classes } = useStyles();
  const [userType, setUserType] = useState<UserType>('all');
  const [data, setData] = useState<AdminUserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchData = useCallback(async () => {
    if (!searchTerm) {
      setData([]);
      setRowCount(0);

      return;
    }

    setIsLoading(true);
    try {
      const response = await AdminUserSearchService.search({
        searchTerm,
        userType: userType === 'all' ? undefined : userType,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      setData(response.data.data || []);
      setRowCount(response.data.meta?.size || 0);
    } catch {
      toast.error('Error searching users');
      setData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, userType, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPagination(prev => (prev.pageIndex !== 0 ? { ...prev, pageIndex: 0 } : prev));
  }, [searchTerm, userType]);

  const handleCopyId = async (id: number) => {
    if (!navigator?.clipboard?.writeText) {
      toast.error('Clipboard is not available in this browser.');

      return;
    }

    try {
      await navigator.clipboard.writeText(id.toString());
      toast.success(`Copied user ID: ${id}`);
    } catch {
      toast.error('Failed to copy user ID to clipboard.');
    }
  };

  const getDisplayName = (result: AdminUserSearchResult): string => {
    const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim();

    return fullName || result.name || 'Unknown';
  };

  const getUserTypeIcon = (result: AdminUserSearchResult) => {
    if (result.parent) return <Home fontSize='small' color='primary' />;
    if (result.sitter) return <ChildCare fontSize='small' color='secondary' />;

    return null;
  };

  const getUserTypeLabel = (result: AdminUserSearchResult): string => {
    if (result.parent && result.sitter) return 'Both';
    if (result.parent) return 'Parent';
    if (result.sitter) return 'Sitter';

    return 'Agency';
  };

  const getUserTypeChipColor = (result: AdminUserSearchResult): 'primary' | 'secondary' | 'default' => {
    if (result.parent) return 'primary';
    if (result.sitter) return 'secondary';

    return 'default';
  };

  const columns = [
    {
      id: 'user',
      header: 'User',
      columns: [
        {
          header: 'ID',
          accessorKey: 'id',
          size: 80,
          Cell: ({ row }) => (
            <Box display='flex' alignItems='center'>
              <Typography variant='body2'>{row.original.id}</Typography>
              <Tooltip title='Copy ID'>
                <IconButton
                  className={classes.copyButton}
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    handleCopyId(row.original.id);
                  }}
                >
                  <ContentCopy fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
        {
          header: 'Name',
          accessorFn: row => getDisplayName(row),
          Cell: ({ row }) => (
            <Box className={classes.userCell}>
              <Avatar
                className={classes.avatar}
                src={row.original.pictureUrl}
                alt={getDisplayName(row.original)}
              >
                {(row.original.firstName || row.original.name || '?').charAt(0)}
              </Avatar>
              <Box>
                <Typography
                  variant='body2'
                  className={!row.original.status ? classes.deactivated : ''}
                >
                  {getDisplayName(row.original)}
                  {!row.original.status && (
                    <Tooltip title='Deactivated'>
                      <Block fontSize='small' color='error' style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                    </Tooltip>
                  )}
                </Typography>
              </Box>
            </Box>
          ),
        },
        {
          header: 'Email',
          accessorKey: 'email',
          size: 200,
        },
        {
          header: 'Type',
          accessorFn: row => getUserTypeLabel(row),
          size: 100,
          Cell: ({ row }) => (
            <Chip
              icon={getUserTypeIcon(row.original)}
              label={getUserTypeLabel(row.original)}
              size='small'
              className={classes.chip}
              color={getUserTypeChipColor(row.original)}
            />
          ),
        },
        {
          header: 'Status',
          accessorFn: row => (row.status ? 'Active' : 'Deactivated'),
          size: 100,
          Cell: ({ row }) => (
            <Typography
              variant='body2'
              className={!row.original.status ? classes.deactivated : ''}
            >
              {row.original.status ? 'Active' : 'Deactivated'}
            </Typography>
          ),
        },
      ],
    },
  ];

  if (!searchTerm) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Tabs
        value={userType}
        onChange={(_, value) => setUserType(value)}
        className={classes.tabs}
        indicatorColor='primary'
        textColor='primary'
      >
        <Tab value='all' label='All Users' />
        <Tab value='parent' label='Parents' icon={<Home fontSize='small' />} iconPosition='start' />
        <Tab value='sitter' label='Sitters' icon={<ChildCare fontSize='small' />} iconPosition='start' />
        <Tab value='agency' label='Agencies' />
      </Tabs>

      <MaterialReactTable
        columns={columns}
        data={data}
        getRowId={row => row.id?.toString()}
        manualPagination
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableSorting={false}
        onPaginationChange={setPagination}
        rowCount={rowCount}
        state={{
          isLoading,
          pagination,
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => onSelectUser(row.original),
          sx: { cursor: 'pointer' },
        })}
        muiTableProps={{
          sx: {
            tableLayout: 'auto',
          },
        }}
        defaultColumn={{
          minSize: 10,
          size: 100,
        }}
      />
    </div>
  );
}

export default UserSearchResults;
